import { mkConfig, generateCsv, asBlob } from 'export-to-csv'
import { Row } from '@tanstack/react-table'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRouteContext } from '@tanstack/react-router'

import { ISODateStringToYYYYMMDD } from '@/utils/util'
import { fetchAuthenticatedData } from '@/queries/shared'
import { DownloadBlob } from '@/components/components'
import { EffectiveDateRecord, LeaDataModel, defaultLeaData, CLEN, SCLR, MutationResponse } from '@/models'
import { FileStatusEnum, FileSubmission } from '@/models/file-submission.tsx'

export type CLRM = {
    Id: string
    ReportingLEA: string
    PreschoolCode: string
    ProgramYear: string
    LocalClassroomId: string
    ClassroomName: string
    VolunteerFrequency: string
    ProgramTypeOffered1: string
    ProgramTypeOffered2: string
    ProgramDurationOffered1: string
    ProgramDurationOffered2: string
    ContractTypeOffered1: string
    ContractTypeOffered2: string
    SettingTypeOffered: string
    MHCSOffered: string
    LanguageProgramType: string
    LanguageUsedInClassroom1: string
    LanguageUsedInClassroom2: string
    LanguageUsedInClassroom3: string
    LanguageUsedInClassroom4: string
    LanguageUsedInClassroom5: string
    StaffAssignments: Array<SCLR> | undefined
    ChildAssignments: Array<CLEN> | undefined
} & EffectiveDateRecord

export type CLRM_Reference = {
    PreschoolCode: string
    LocalClassroomId: string
}

export function CLRM_Key(x: CLRM_Reference): string {
    return `${x.PreschoolCode}-${x.LocalClassroomId}`
}

export function Parse_CLRM_Key(x: string): CLRM_Reference {
    return {
        PreschoolCode: x.substring(0, 7),
        LocalClassroomId: x.substring(8)
    }
}

export const CLRM_TEMPLATE: CLRM = {
    Id: '',
    ReportingLEA: '',
    PreschoolCode: '',
    ProgramYear: '',
    LocalClassroomId: '',
    ClassroomName: '',
    VolunteerFrequency: '',
    ProgramTypesOffered: '',
    ProgramDurationsOffered: '',
    ContractTypesOffered: '',
    SettingTypeOffered: '',
    LanguageProgramType: '',
    MHCSOffered: '',
    LanguageUsedInClassroom1: '',
    LanguageUsedInClassroom2: '',
    LanguageUsedInClassroom3: '',
    LanguageUsedInClassroom4: '',
    LanguageUsedInClassroom5: '',
    EffectiveStartDate: null,
    EffectiveEndDate: '',
    StaffAssignments: [],
    ChildAssignments: [],
}

export function updateCLRM(cr: CLRM, hasRole: (...roles: string[]) => boolean | undefined, lea_data: LeaDataModel): CLRM {
    cr.ChildAssignments = !hasRole('CLENView') ? undefined : lea_data.clen.filter((e) => e.PreschoolCode === cr.PreschoolCode && e.LocalClassroomId === cr.LocalClassroomId)
    cr.StaffAssignments = !hasRole('SCLRView') ? undefined : lea_data.sclr.filter((e) => e.PreschoolCode === cr.PreschoolCode && e.LocalClassroomId === cr.LocalClassroomId)
    return cr
}

export function asSerializeable(clrm: CLRM) {
    return {
        'ReportingLEA': clrm.ReportingLEA,
        'PreschoolCode': clrm.PreschoolCode,
        'ProgramYear': clrm.ProgramYear,
        'ClassroomName': clrm.ClassroomName,
        'LocalClassroomId': clrm.LocalClassroomId,
        'VolunteerFrequency': clrm.VolunteerFrequency,
        'ProgramTypeOffered1': clrm.ProgramTypeOffered1,
        'ProgramTypeOffered2': clrm.ProgramTypeOffered2,
        'ProgramDurationOffered1': clrm.ProgramDurationOffered1,
        'ProgramDurationOffered2': clrm.ProgramDurationOffered2,
        'ContractTypeOffered1': clrm.ContractTypeOffered1,
        'ContractTypeOffered2': clrm.ContractTypeOffered2,
        'SettingTypeOffered': clrm.SettingTypeOffered,
        'LanguageProgramType': clrm.LanguageProgramType,
        'MHCSOffered': (clrm.MHCSOffered === 'Y'),
        'LanguageUsedInClassroom1': clrm.LanguageUsedInClassroom1,
        'LanguageUsedInClassroom2': clrm.LanguageUsedInClassroom2,
        'LanguageUsedInClassroom3': clrm.LanguageUsedInClassroom3,
        'LanguageUsedInClassroom4': clrm.LanguageUsedInClassroom4,
        'LanguageUsedInClassroom5': clrm.LanguageUsedInClassroom5,
        'EffectiveStartDate': clrm.EffectiveStartDate,
        'EffectiveEndDate': clrm.EffectiveEndDate,
    }
}

export function mutationFunctions(leaData: LeaDataModel, setSubmitResult: (resp: MutationResponse<CLRM>) => void) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    const mutation = useMutation({
        mutationFn: ({ urlVal, clrm }: { urlVal: string, clrm: CLRM }) => {
            return fetchAuthenticatedData<MutationResponse<CLRM>>(leaContext, urlVal, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(asSerializeable(clrm))
            }).then(resp => {
                setSubmitResult(resp)
            })
        },
        onSuccess: (data) => {
        },
        onError: (error) => {
            console.error(error)
        },
    })
    return {
        mutationState: mutation,
        handleSave: async function (clrm: CLRM, isDelete: boolean = false) {
            var urlVal = `/${leaContext.currentUserOrg.esiIdPath}/dashboard/${isDelete ? 'D' : 'U'}/ajax/EditClassroomAjax`
            //const idx = data.clrm.findIndex((e) => e.id === x.id)
            //if (idx == -1) {
            //    console.log(`Classroom with ID ${classroomId} not found`)
            //    return
            //}
            //if (idx == -1) {
            //    data.clrm.push(Object.assign({}, x))
            //} else {
            //    data.clrm[idx] = Object.assign({}, x)
            //}
            //const clrm = leaData.clrm[idx]
            mutation.mutate({ urlVal, clrm })
        }
    }
}

